/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @ngdoc component
 * @name infrastructureDetail
 * @param {Cloud} resolveCloud - Cloud item loaded by resolve.
 * @desc
 *     Component that manages the Infrastructure pages Service Engine List, Service Engine Group
 *     List, Network List, and Routing pages. Shows a dropdown for the user to select a cloud, with
 *     the Default-Cloud selected by default. On cloud change, adds the cloud uuid to the URL params
 *     and checks the state to make sure it's valid based on the Cloud, else redirects.
 */
import * as l10n from './infrastructure-detail.l10n';

const { ENGLISH: dictionary, ...l10nKeys } = l10n;
const componentName = 'infrastructure-detail';

class InfrastructureDetailController {
    constructor($state, CloudCollection, infraCloudState, l10nService, $transitions) {
        this._$state = $state;
        this._CloudCollection = CloudCollection;
        this._infraCloudState = infraCloudState;

        this.cloudRef = '';
        this.showSelectCloudDropdown = false;
        this._stateParams = {};
        this.l10nKeys = l10nKeys;
        this.showCloudSelector = false;
        this.$transitions_ = $transitions;

        l10nService.registerSourceBundles(dictionary);
    }

    $onInit() {
        this.setShowCloudSelector();

        this.destroyTransitionListener = this.$transitions_.onSuccess({}, () => {
            this.setShowCloudSelector();
        });

        this._stateParams = this._$state.params;
        this._infraCloudState.setCloud(this.cloud);
        this.cloudRef = this.cloud.getRef();

        this.cloudCollection = new this._CloudCollection({
            isStatic: true,
            params: {
                sort: 'name',
            },
        });

        const { cloudCollection } = this;

        cloudCollection.load()
            .then(() => {
                this.showSelectCloudDropdown = cloudCollection.getNumberOfItems() > 1;
            });
    }

    /**
     * Handles the changing of clouds from the Select Cloud dropdown.
     * @param {Cloud} cloud - cloud Cloud object.
     */
    handleCloudChange(cloud) {
        const cloudId = cloud.id;
        const params = angular.extend({}, this._stateParams, { cloudId });
        const $state = this._$state;

        $state.go($state.current, params).finally(() => this._infraCloudState.setCloud(cloud));
    }

    /**
     * Set showCloudSelector to determine if the cloud selection dropdown should be shown based on
     * the current state's data.
     */
    setShowCloudSelector() {
        const { current } = this._$state;
        const { data = {} } = current;

        // hasOwnProperty is used here as it seems a state's data is extended from its parent's.
        this.showCloudSelector = Object.prototype.hasOwnProperty.call(data, 'showCloudSelector') &&
            data.showCloudSelector || false;
    }

    $onDestroy() {
        this.cloud.destroy();
        this.cloudCollection.destroy();
        this.destroyTransitionListener();
    }
}

InfrastructureDetailController.$inject = [
    '$state',
    'CloudCollection',
    'infraCloudState',
    'l10nService',
    '$transitions',
];

angular.module('aviApp')
    .component('infrastructureDetail', {
        bindings: {
            cloud: '<resolveCloud',
        },
        controller: InfrastructureDetailController,
        templateUrl: `src/components/pages/infrastructure/${componentName}/${componentName}.html`,
    });
